import { useState, useRef, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "../../../components/feature/Navbar";
import Footer from "../../../components/feature/Footer";
import ProductGallery from "./components/ProductGallery";
import ProductPurchasePanel from "./components/ProductPurchasePanel";
import ProductTabs from "./components/ProductTabs";
import RelatedProducts from "./components/RelatedProducts";
import RecentlyViewedSection from "./components/RecentlyViewedSection";
import CustomersAlsoBought from "./components/CustomersAlsoBought";
import StickyCartBar from "./components/StickyCartBar";
import CompareBar from "../components/CompareBar";
import CompareModal from "../components/CompareModal";
import { useShopifyProducts } from "../../../context/ShopifyProductsContext";
import { useRecentlyViewed } from "../../../hooks/useRecentlyViewed";

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [added, setAdded] = useState(false);
  const [panelInView, setPanelInView] = useState(true);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const { getProductById, getProductDetailById, products, loading } = useShopifyProducts();

  const product = id ? getProductById(id) : undefined;
  const detail = id ? getProductDetailById(id) : undefined;

  // Track this visit + get prior views — runs after product resolves
  const recentlyViewedIds = useRecentlyViewed(product ? id : undefined);

  // Show sticky bar when the purchase panel is no longer visible
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setPanelInView(entry.isIntersecting),
      { threshold: 0, rootMargin: "-80px 0px 0px 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [product]);

  // Back to top visibility
  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 500);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  // Build Product structured data for GMC / Google
  const productSchema = product && detail
    ? {
        "@context": "https://schema.org/",
        "@type": "Product",
        "@id": `https://ironhavensupply.com/shop/${product.id}`,
        name: product.name,
        image: detail.images.filter(Boolean),
        description: detail.description,
        sku: product.id,
        mpn: product.id,
        brand: {
          "@type": "Brand",
          name: product.brand,
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: product.rating.toFixed(1),
          reviewCount: product.reviews,
          bestRating: "5",
          worstRating: "1",
        },
        offers: {
          "@type": "Offer",
          "@id": `https://ironhavensupply.com/shop/${product.id}#offer`,
          url: `https://ironhavensupply.com/shop/${product.id}`,
          priceCurrency: "USD",
          price: product.price.toFixed(2),
          priceValidUntil: "2027-12-31",
          itemCondition: "https://schema.org/NewCondition",
          availability: product.inStock
            ? "https://schema.org/InStock"
            : "https://schema.org/OutOfStock",
          seller: {
            "@type": "Organization",
            name: "Iron Haven Supply",
            "@id": "https://ironhavensupply.com/#organization",
          },
          shippingDetails: {
            "@type": "OfferShippingDetails",
            shippingRate: {
              "@type": "MonetaryAmount",
              value: "0",
              currency: "USD",
            },
            deliveryTime: {
              "@type": "ShippingDeliveryTime",
              handlingTime: {
                "@type": "QuantitativeValue",
                minValue: 1,
                maxValue: 2,
                unitCode: "DAY",
              },
              transitTime: {
                "@type": "QuantitativeValue",
                minValue: 3,
                maxValue: 7,
                unitCode: "DAY",
              },
            },
          },
          hasMerchantReturnPolicy: {
            "@type": "MerchantReturnPolicy",
            applicableCountry: "US",
            returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
            merchantReturnDays: 30,
            returnMethod: "https://schema.org/ReturnByMail",
            returnFees: "https://schema.org/FreeReturn",
          },
        },
      }
    : null;

  // BreadcrumbList schema
  const breadcrumbSchema = product
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://ironhavensupply.com/" },
          { "@type": "ListItem", position: 2, name: "Shop", item: "https://ironhavensupply.com/shop" },
          {
            "@type": "ListItem",
            position: 3,
            name:
              product.category === "solar" ? "Solar & Battery"
              : product.category === "gas" ? "Gas Generators"
              : "Whole Home Systems",
            item: `https://ironhavensupply.com/shop?category=${product.category}`,
          },
          { "@type": "ListItem", position: 4, name: product.name },
        ],
      }
    : null;

  // Dynamic SEO values
  const pageTitle = product
    ? `${product.name} | ${product.brand} | Iron Haven Supply`
    : "Product | Iron Haven Supply";
  const pageDesc = product && detail
    ? `Buy the ${product.name} by ${product.brand}. ${detail.description.slice(0, 130).replace(/\s\w+$/, "")}... Free shipping & manufacturer warranty included.`
    : "Shop backup power systems at Iron Haven Supply. Free shipping & manufacturer warranty on every order.";
  const canonicalUrl = product
    ? `https://ironhavensupply.com/shop/${product.id}`
    : "https://ironhavensupply.com/shop";
  const ogImage = product && detail?.images?.[0] ? detail.images[0] : "https://ironhavensupply.com/og-image.jpg";

  if (loading) {
    return (
      <main className="font-sans bg-white min-h-screen">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-40 text-center">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-4 animate-spin text-amber-500">
            <i className="ri-loader-4-line text-4xl"></i>
          </div>
          <p className="text-gray-400 text-sm">Loading product...</p>
        </div>
        <Footer />
      </main>
    );
  }

  if (!product || !detail) {
    return (
      <main className="font-sans bg-white min-h-screen">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-40 text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-gray-200">
            <i className="ri-error-warning-line text-6xl"></i>
          </div>
          <h1 className="text-2xl font-extrabold text-gray-900 mb-2">Product Not Found</h1>
          <p className="text-gray-500 mb-6">This product doesn&apos;t exist or may have been removed.</p>
          <button
            onClick={() => navigate("/shop")}
            className="bg-amber-500 hover:bg-amber-400 text-white font-bold px-6 py-3 rounded-lg transition-colors cursor-pointer"
          >
            Back to Shop
          </button>
        </div>
        <Footer />
      </main>
    );
  }

  const saveDollars = product.originalPrice ? product.originalPrice - product.price : null;

  const handleAddToCart = (_qty: number) => {
    setAdded(true);
    setTimeout(() => setAdded(false), 2500);
  };

  const categoryLabel =
    product.category === "solar" ? "Solar & Battery"
    : product.category === "gas" ? "Gas Generators"
    : "Whole Home";

  return (
    <main className="font-sans bg-white min-h-screen">
      {/* ── Dynamic head tags (React 19 native hoisting) ── */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content="product" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Iron Haven Supply" />
      {product && <meta property="product:price:amount" content={product.price.toFixed(2)} />}
      {product && <meta property="product:price:currency" content="USD" />}
      {product && <meta property="product:availability" content={product.inStock ? "in stock" : "out of stock"} />}
      {product && <meta property="product:brand" content={product.brand} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDesc} />
      <meta name="twitter:image" content={ogImage} />

      {/* Product + BreadcrumbList structured data */}
      {productSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }}
        />
      )}
      {breadcrumbSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />
      )}

      <Navbar />
      <StickyCartBar product={product} visible={!panelInView} />

      {/* Utility bar */}
      <div className="bg-gray-900 pt-16 hidden md:block">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center gap-6 text-xs text-gray-300 overflow-x-auto">
          <a href="tel:8666054975" className="flex items-center gap-1.5 hover:text-amber-400 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-phone-line text-amber-400"></i> (866) 605-4975
          </a>
          <span className="flex items-center gap-1.5 whitespace-nowrap"><i className="ri-truck-line text-amber-400"></i> Free Shipping on All Orders</span>
          <span className="flex items-center gap-1.5 whitespace-nowrap"><i className="ri-price-tag-3-line text-amber-400"></i> Price Match Guarantee</span>
          <span className="flex items-center gap-1.5 whitespace-nowrap"><i className="ri-shield-check-line text-amber-400"></i> Manufacturer Warranty Included</span>
          <span className="flex items-center gap-1.5 whitespace-nowrap"><i className="ri-arrow-go-back-line text-amber-400"></i> 30-Day Returns</span>
        </div>
      </div>
      <div className="block md:hidden pt-20"></div>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
        <nav className="flex items-center gap-1.5 text-xs text-gray-400 flex-wrap">
          <Link to="/" className="hover:text-amber-500 transition-colors cursor-pointer whitespace-nowrap">Home</Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <Link to="/shop" className="hover:text-amber-500 transition-colors cursor-pointer whitespace-nowrap">Shop</Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="hover:text-amber-500 transition-colors cursor-pointer whitespace-nowrap" onClick={() => navigate("/shop")}>{categoryLabel}</span>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-gray-700 font-medium line-clamp-1">{product.name}</span>
        </nav>
      </div>

      {/* PDP top section */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          <div className="lg:sticky lg:top-28">
            <ProductGallery
              images={detail.images}
              productName={product.name}
              badge={product.badge}
              isNew={product.isNew}
              saveDollars={saveDollars}
              inStock={product.inStock}
            />
          </div>
          <div ref={panelRef}>
            <ProductPurchasePanel
              product={product}
              onAddToCart={handleAddToCart}
              added={added}
            />
          </div>
        </div>

        <ProductTabs product={product} detail={detail} />

        <CustomersAlsoBought
          currentProduct={product}
          allProducts={products}
        />

        <RecentlyViewedSection
          recentIds={recentlyViewedIds}
          allProducts={products}
        />

        <RelatedProducts
          currentId={product.id}
          category={product.category}
          allProducts={products}
        />
      </div>

      {product.inStock && (
        <div className="fixed bottom-0 left-0 right-0 md:hidden bg-white border-t border-gray-100 px-4 py-3 flex items-center gap-3 z-40">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 truncate">{product.name}</p>
            <p className="font-extrabold text-gray-900 text-base">${product.price.toLocaleString()}</p>
          </div>
          <button
            onClick={() => handleAddToCart(1)}
            className={`shrink-0 px-6 py-3 rounded-xl font-bold text-sm transition-all cursor-pointer ${
              added ? "bg-emerald-500 text-white" : "bg-amber-500 hover:bg-amber-400 text-white"
            }`}
          >
            {added ? "Added!" : "Add to Cart"}
          </button>
        </div>
      )}

      <CompareBar />
      <CompareModal />
      <Footer />

      {/* Back to top */}
      <button
        onClick={scrollToTop}
        aria-label="Back to top"
        className={`fixed right-6 z-50 w-11 h-11 flex items-center justify-center rounded-full bg-gray-900 text-white shadow-md transition-all duration-300 cursor-pointer hover:bg-amber-500 ${
          showBackToTop ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none"
        } bottom-24 md:bottom-8`}
      >
        <i className="ri-arrow-up-line text-base"></i>
      </button>
    </main>
  );
}
